<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as CancelCloseStopDialogFormInitialData,
  ValidData as CancelCloseStopDialogFormValidData,
} from '@/components/dialogForms/CancelCloseStopDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import JobsBasicList from '@/components/basicLists/JobsBasicList.vue';
import KBtn from '@/components/btns/KBtn.vue';
import CancelCloseStopDialogForm from '@/components/dialogForms/CancelCloseStopDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export type DialogType = 'cancel' | 'futile';

export interface Props {
  type?: DialogType;
  isConfirmLoading?: boolean;
  jobs?: { jobId: number; jobDate: string; driver: number }[];
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  type: 'cancel',
  isConfirmLoading: false,
  jobs: () => [],
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: CancelCloseStopDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: CancelCloseStopDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<CancelCloseStopDialogFormInitialData>(() => {
  return {
    name: undefined,
    reason: undefined,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.cancelCloseStopDialog"
    data-testid="cancelCloseStopDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.type === 'futile'">Close jobs as futile</template>
        <template v-else>Cancel jobs</template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <JobsBasicList :jobs="props.jobs" class="mt-0 mb-6" />
      <div :class="$style.text">Please, fill out required form to confirm.</div>
      <CancelCloseStopDialogForm
        :is-futile="props.type === 'futile'"
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.cancelCloseStopDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
